/**
 * The fold-able sections on a Properties-tab card — the same header pattern as
 * the Applicants card's "Credited experience" / "Prior loans & servicing"
 * (CollapsibleSectionHeader: arrow on the left, whole row toggles, a summary
 * pill while folded). Both start folded.
 *
 *   PropertyMarketSection — the Zillow market data for THIS property's zip, the
 *     same per-zip card the property sidebar's Market Data tab renders.
 *     `GET /underwriting/get_zillow_zipcode_data/{dealId}` answers for every
 *     zip on the loan, so the tab fetches it ONCE (useLoanMarketData) and each
 *     card picks its own row.
 *   PropertyUnitsSection — the property's `units[]`, read-only here. Edit /
 *     Add on the header open the property sidebar on its Units tab (Add with a
 *     new row already open) — editing happens in that table.
 */
import {
  AddCircleOutline,
  EditOutlined,
  SyncOutlined,
} from "@mui/icons-material";
import { Box, Button, CircularProgress } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";

import AgGridTableWrapper from "../../../../../components/agGrid/AgGridTableWrapper";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import { MarketDataCard } from "../../../../../components/sidebar/LoanSubjectPropertySidebar/LoanSubjectPropertySidebar";
import formatDollar from "../../../../../_src_shared/utils/functions/formatDollar";
import { useUnderwritingHook } from "../../../../../hooks/useUnderwritingHook";
import { slate, status } from "../../../../../theme/colors";
import {
  CARD_GRID_COL_DEF,
  CARD_GRID_SX,
  CARD_TABLE_BOX_SX,
} from "../../../shared/cardGrid";
import CollapsibleSectionHeader, {
  SummaryPill,
} from "../../../shared/CollapsibleSectionHeader";

const stop = (e) => e.stopPropagation();

const money = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n !== 0 ? formatDollar(Math.round(n)) : null;
};

/* ── market data ───────────────────────────────────────── */

/**
 * One Zillow fetch for the whole loan, and only on demand: nothing is
 * requested until a card's Market data section is first expanded
 * (`ensureLoaded`). Every other card then reads the same rows. `status`:
 * idle (never asked) | loading | success | error.
 */
export const useLoanMarketData = (dealId) => {
  const { getZillowZipcodeData } = useUnderwritingHook();
  const [state, setState] = useState({ status: "idle", rows: [], error: null });

  // A different loan starts un-asked again.
  useEffect(() => {
    setState({ status: "idle", rows: [], error: null });
  }, [dealId]);

  const load = useCallback(() => {
    if (!dealId) return;
    setState((s) => ({ ...s, status: "loading", error: null }));
    getZillowZipcodeData({
      dealId,
      onSuccessFn: (res) =>
        setState({
          status: "success",
          rows: Array.isArray(res) ? res : res ? [res] : [],
          error: null,
        }),
      onFailFn: (err) =>
        setState({
          status: "error",
          rows: [],
          error: err?.message || "Failed to load market data.",
        }),
    });
    // getZillowZipcodeData is recreated each render by its hook; keying on the
    // deal is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  // First expand → fetch; later expands reuse the result.
  const ensureLoaded = useCallback(() => {
    if (state.status === "idle") load();
  }, [state.status, load]);

  return { ...state, refresh: load, ensureLoaded };
};

const zipRowFor = (rows, zip) => {
  if (!zip) return null;
  const z = String(zip);
  return (
    (rows || []).find(
      (r) => String(r?.ZipCode) === z || String(r?.searched_zip) === z,
    ) || null
  );
};

export const PropertyMarketSection = ({ property, market }) => {
  const [open, setOpen] = useState(false);
  const zip = property?.address?.zip;
  const row = useMemo(() => zipRowFor(market?.rows, zip), [market?.rows, zip]);
  if (!zip) return null;

  const idle = !market?.status || market.status === "idle";
  // "idle" while open means the request is about to go out this render.
  const loading = market?.status === "loading" || (open && idle);
  const hpa = typeof row?.HPA === "number" ? row.HPA : null;
  const median = money(row?.zillow_median);

  // Nothing fetched yet → no pill; the section is just its title.
  const summary = idle ? null : loading ? (
    <SummaryPill>Loading…</SummaryPill>
  ) : row ? (
    <SummaryPill
      color={
        hpa == null ? undefined : hpa >= 0 ? status.success.fg : status.error.fg
      }
    >
      {[
        median && `${median} median`,
        hpa != null && `${hpa >= 0 ? "+" : ""}${(hpa * 100).toFixed(1)}% HPA`,
      ]
        .filter(Boolean)
        .join(" · ") || `ZIP ${zip}`}
    </SummaryPill>
  ) : (
    <SummaryPill>No data for {zip}</SummaryPill>
  );

  return (
    <Box onClick={stop} onMouseDown={stop} sx={{ cursor: "default" }}>
      <Flx ac sx={{ mb: open ? 1.25 : 0 }}>
        <CollapsibleSectionHeader
          title="Market data"
          open={open}
          onToggle={() => {
            // The Zillow request fires on the first expand, never before.
            if (!open) market?.ensureLoaded?.();
            setOpen((v) => !v);
          }}
          summary={summary}
        />
      </Flx>
      {open &&
        (loading ? (
          <Flx ac g={1} sx={{ py: 1 }}>
            <CircularProgress
              size={14}
              thickness={5}
              sx={{ color: slate[400] }}
            />
            <Txt sx={{ fontSize: 12.5, color: slate[500] }}>
              Loading Zillow market data…
            </Txt>
          </Flx>
        ) : row ? (
          <MarketDataCard zip={row} isSubject={false} />
        ) : (
          <Flx ac jb g={1.5} sx={{ py: 0.5 }}>
            <Txt sx={{ fontSize: 12.5, color: slate[500] }}>
              {market?.status === "error"
                ? market.error
                : `No Zillow data for ZIP ${zip} yet.`}
            </Txt>
            <Button
              size="small"
              variant="outlined"
              startIcon={<SyncOutlined />}
              onClick={(e) => {
                stop(e);
                market?.refresh?.();
              }}
            >
              Refresh
            </Button>
          </Flx>
        ))}
    </Box>
  );
};

PropertyMarketSection.propTypes = {
  property: PropTypes.object,
  market: PropTypes.shape({
    status: PropTypes.string,
    rows: PropTypes.array,
    error: PropTypes.string,
    refresh: PropTypes.func,
    ensureLoaded: PropTypes.func,
  }),
};

/* ── units ─────────────────────────────────────────────── */

const unitUse = (u) =>
  [u?.residentialUnit && "Residential", u?.commercialUnit && "Commercial"]
    .filter(Boolean)
    .join(" / ") || "—";

const UNIT_COLUMNS = [
  {
    headerName: "Unit",
    colId: "unit",
    valueGetter: (p) => p.data?.unitNumber ?? "—",
    flex: 0.8,
    minWidth: 80,
  },
  {
    headerName: "Use",
    colId: "use",
    valueGetter: (p) => unitUse(p.data),
    flex: 1,
    minWidth: 110,
  },
  {
    headerName: "Lease",
    field: "leaseType",
    valueFormatter: (p) => p.value || "—",
    flex: 1.2,
    minWidth: 120,
  },
  {
    headerName: "Sq ft",
    field: "unitSquareFootage",
    valueFormatter: (p) => (p.value ? Number(p.value).toLocaleString() : "—"),
    cellClass: "num",
    width: 90,
  },
  {
    headerName: "In-place rent",
    field: "unitMonthlyInPlaceRent",
    valueFormatter: (p) => money(p.value) || "—",
    cellClass: "num",
    width: 120,
  },
  {
    headerName: "Market rent",
    field: "unitMonthlyMarketRent",
    valueFormatter: (p) => money(p.value) || "—",
    cellClass: "num",
    width: 120,
  },
  {
    headerName: "Vacant",
    field: "unitVacant",
    valueFormatter: (p) =>
      p.value === true ? "Vacant" : p.value === false ? "Occupied" : "—",
    width: 100,
  },
];

export const PropertyUnitsSection = ({ units, onEditUnits, onAddUnits }) => {
  const [open, setOpen] = useState(false);
  const summary = useMemo(() => {
    const list = units || [];
    const sum = (key) =>
      list.reduce((acc, u) => {
        const n = Number(u?.[key]);
        return Number.isFinite(n) ? acc + n : acc;
      }, 0);
    const vacant = list.filter((u) => u?.unitVacant === true).length;
    if (!list.length) return "No units yet";
    return [
      `${list.length} unit${list.length === 1 ? "" : "s"}`,
      vacant > 0 && `${vacant} vacant`,
      money(sum("unitMonthlyInPlaceRent")) &&
        `${money(sum("unitMonthlyInPlaceRent"))}/mo in-place`,
      money(sum("unitMonthlyMarketRent")) &&
        `${money(sum("unitMonthlyMarketRent"))}/mo market`,
    ]
      .filter(Boolean)
      .join(" · ");
  }, [units]);

  // A unit may have neither _id nor number yet; key rows by position then.
  const rows = useMemo(
    () =>
      (units || []).map((u, i) => ({
        ...u,
        __rowKey: String(u?._id || `idx-${i}`),
      })),
    [units],
  );

  const hasUnits = !!units?.length;

  return (
    <Box onClick={stop} onMouseDown={stop} sx={{ cursor: "default" }}>
      <Flx ac g={1} sx={{ mb: open && hasUnits ? 1.25 : 0 }}>
        <CollapsibleSectionHeader
          title="Units"
          open={open}
          onToggle={() => setOpen((v) => !v)}
          meta={<SummaryPill>{summary}</SummaryPill>}
        />
        {/* Outside the header button, so they never toggle the fold. */}
        {hasUnits && onEditUnits ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditOutlined />}
            onClick={(e) => {
              stop(e);
              onEditUnits();
            }}
          >
            Edit
          </Button>
        ) : null}
        {onAddUnits ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddCircleOutline />}
            onClick={(e) => {
              stop(e);
              onAddUnits();
            }}
          >
            Add unit
          </Button>
        ) : null}
      </Flx>
      {open && hasUnits && (
        <Box sx={CARD_TABLE_BOX_SX}>
          <AgGridTableWrapper
            compactTable
            noBorder
            suppressMinHeightWhenPopulated={units}
            sx={CARD_GRID_SX}
          >
            <AgGridReact
              rowData={rows}
              columnDefs={UNIT_COLUMNS}
              defaultColDef={CARD_GRID_COL_DEF}
              getRowId={(p) => p.data.__rowKey}
              domLayout="autoHeight"
              suppressCellFocus
            />
          </AgGridTableWrapper>
        </Box>
      )}
    </Box>
  );
};

PropertyUnitsSection.propTypes = {
  units: PropTypes.array,
  onEditUnits: PropTypes.func,
  onAddUnits: PropTypes.func,
};
